/**
 * EventsContext - Shared events data provider
 *
 * This context manages event fetching and shares data between:
 * - EventList (main window UI)
 * - EventsDataProvider (popup communication)
 *
 * Key features:
 * - Single source of truth for events data
 * - Trailing mode support for live updates
 * - Automatic push to popup windows when data changes
 * - Listens for date range changes from popup
 */
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import { DateRange } from 'react-day-picker';
import {
  DetectedEvent,
  useGetEvents,
  useGetEventsForPeriod,
} from '@ais-ui/websockets';
import {
  CombinedGroundingEventAndConfiguration,
  useGetGroundingEventsAndConfigurations,
} from './Detectors/Grounding/GroundingEvent';
import { useSettings } from '../Settings/SettingsProvider';
import { MainWindowActions, DataType, usePopupMessages, MessageType, PopupMessage } from '../Popup/PopupEvents';
import { isPopupTypeActive } from '../Popup/usePopupEventHandler';
import { PopupType } from '@ais-ui/popups';

interface EventsContextValue {
  // Events data
  events: DetectedEvent[];
  groundingEvents: CombinedGroundingEventAndConfiguration[];
  isLoading: boolean;
  
  // Date range
  date: DateRange | undefined;
  setDate: React.Dispatch<React.SetStateAction<DateRange | undefined>>;
  
  // Trailing mode
  isTrailing: boolean;
  setIsTrailing: React.Dispatch<React.SetStateAction<boolean>>;
  
  // Default lookback time from settings
  defaultLookBackTime: number;
}

const EventsContext = createContext<EventsContextValue | null>(null);

const getDefaultDateRange = (lookBackMinutes: number): DateRange => ({
  from: new Date(Date.now() - lookBackMinutes * 60 * 1000),
  to: new Date(),
});

interface EventsProviderProps {
  children: ReactNode;
}

export const EventsProvider: React.FC<EventsProviderProps> = ({ children }) => {
  const { settings } = useSettings();
  
  const defaultLookBackTime = useMemo(
    () => settings['ribbonPlugin.settings']?.event_lookback_minutes || 720,
    [settings],
  );

  const [date, setDate] = useState<DateRange | undefined>(() =>
    getDefaultDateRange(defaultLookBackTime),
  );
  const [isTrailing, setIsTrailing] = useState(true);
  const [events, setEvents] = useState<DetectedEvent[]>([]);
  const [groundingEvents, setGroundingEvents] = useState<
    CombinedGroundingEventAndConfiguration[]
  >([]);
  const [queryDate, setQueryDate] = useState(date);

  // Subscribe to real-time events
  useGetEvents();

  // Fetch events for period
  const { data, isLoading, isFetching } = useGetEventsForPeriod(
    queryDate?.from,
    queryDate?.to,
  );

  // Fetch grounding events
  const { groundingEventsAndConfigurations, groundingIsLoading } =
    useGetGroundingEventsAndConfigurations(queryDate);

  // Update query date based on trailing mode
  useEffect(() => {
    if (isTrailing) {
      // In trailing mode, only update if we're not currently fetching
      if (!isFetching) {
        setQueryDate(date);
      }
    } else {
      // In normal mode, update immediately
      setQueryDate(date);
    }
  }, [date, isTrailing, isFetching]);

  // Update events state
  useEffect(() => {
    // When trailing is enabled, we don't want to set the events empty on loading
    // as it is disruptive for the user
    if (isTrailing && isLoading) {
      return;
    }
    setEvents(data?.events || []);
  }, [data, isLoading, isTrailing]);

  // Update grounding events state
  useEffect(() => {
    if (isTrailing && groundingIsLoading) {
      return;
    }
    setGroundingEvents(groundingEventsAndConfigurations || []);
  }, [groundingEventsAndConfigurations, groundingIsLoading, isTrailing]);

  // Listen for date range changes from popup
  usePopupMessages(
    useCallback((msg: PopupMessage) => {
      if (msg.type !== MessageType.SET_EVENT_DATE_RANGE) return;
      
      const payload = msg.payload as { from: string; to: string; isTrailing: boolean };
      const newDate: DateRange = {
        from: payload.from ? new Date(payload.from) : undefined,
        to: payload.to ? new Date(payload.to) : undefined,
      };
      setDate(newDate);
      setIsTrailing(payload.isTrailing);
    }, []),
    []
  );

  // Push updates to popup when events change
  useEffect(() => {
    // Only push if there's an active events popup
    if (!isPopupTypeActive(PopupType.Events)) {
      return;
    }

    const currentEvents = data?.events || [];
    const activeEventsCount = currentEvents.filter((e) => !e.deactivated).length;
    const deactivatedEventsCount = currentEvents.filter((e) => e.deactivated).length;

    // Generate dataKey based on event content, not date range
    // This prevents unnecessary updates when trailing mode shifts the window but events are the same
    // Use first and last event IDs + count as a fingerprint
    const firstEventId = currentEvents[0]?.id || '';
    const lastEventId = currentEvents[currentEvents.length - 1]?.id || '';
    const dataKey = `${currentEvents.length}_${activeEventsCount}_${firstEventId}_${lastEventId}`;

    const fromStr = queryDate?.from?.toISOString() || '';
    const toStr = queryDate?.to?.toISOString() || '';

    // Send update to popup
    MainWindowActions.sendDataUpdate(DataType.EVENTS, {
      events: currentEvents,
      totalEvents: currentEvents.length,
      activeEvents: activeEventsCount,
      deactivatedEvents: deactivatedEventsCount,
      dateRange: {
        from: fromStr,
        to: toStr,
      },
      isTrailing,
      settings: {
        tableDensity: settings['ribbonPlugin.settings']?.table_density || 'compact',
      },
      dataKey,
    });
  }, [data?.events, queryDate, settings, isTrailing]);

  const value = useMemo(
    () => ({
      events,
      groundingEvents,
      isLoading: isLoading || groundingIsLoading,
      date,
      setDate,
      isTrailing,
      setIsTrailing,
      defaultLookBackTime,
    }),
    [
      events,
      groundingEvents,
      isLoading,
      groundingIsLoading,
      date,
      isTrailing,
      defaultLookBackTime,
    ],
  );

  return (
    <EventsContext.Provider value={value}>{children}</EventsContext.Provider>
  );
};

export const useEventsContext = (): EventsContextValue => {
  const context = useContext(EventsContext);
  if (!context) {
    throw new Error('useEventsContext must be used within an EventsProvider');
  }
  return context;
};
